/**
 * TrustGrid Diagnostics - Dead-Letter Queue Viewer (Phase 3.3)
 *
 * Displays malformed events that failed validation with detailed error information.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, Tag, Button, Modal, Input, Row, Col, Space, Typography, Tooltip } from 'antd';
import {
  BugOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { getDLQEvents, getDLQSummary, resolveDLQEvent, deleteDLQEvent } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import { TGDataTable, DataCard, DetailModal } from '../../../components/common';
import ProjectSelector from './ProjectSelector';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import { colors } from '../../../theme/designTokens';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

function Diagnostics() {
  const location = useLocation();
  const { selectedProjectId } = useTrustGrid();

  // Build breadcrumbs from navigation state or use defaults
  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = location.state?.breadcrumbs || [
      { label: 'TrustGrid', path: '/trustgrid/dashboard' },
    ];
    return [...baseBreadcrumbs, { label: 'Diagnostics', path: '/trustgrid/diagnostics' }];
  }, [location.state]);

  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);

  // Modals
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [resolveModalVisible, setResolveModalVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, [selectedProjectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch ALL events (client-side pagination/filtering)
      const [eventsRes, summaryRes] = await Promise.all([
        getDLQEvents(1, 10000, {}), // Get all events
        getDLQSummary(),
      ]);

      setEvents(eventsRes.data.events || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch diagnostics data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    try {
      await resolveDLQEvent(selectedEvent.id, resolutionNotes);
      showSuccess('Event marked as resolved');
      setResolveModalVisible(false);
      setResolutionNotes('');
      setSelectedEvent(null);
      fetchData();
    } catch (error) {
      console.error('Failed to resolve event:', error);
      showError(error);
    }
  };

  const handleDelete = async (eventId) => {
    Modal.confirm({
      title: 'Delete DLQ Event',
      content: 'Are you sure you want to delete this event? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteDLQEvent(eventId);
          showSuccess('Event deleted');
          fetchData();
        } catch (error) {
          console.error('Failed to delete event:', error);
          showError(error);
        }
      },
    });
  };

  /**
   * Generate dynamic filter options from data
   */
  const errorTypeFilters = useMemo(() => {
    if (!events || events.length === 0) return [];
    const types = [...new Set(events.map((e) => e.error_type).filter((t) => t))].sort();
    return types.map((type) => ({ label: type, value: type }));
  }, [events]);

  const sourceFilters = useMemo(() => {
    if (!events || events.length === 0) return [];
    const sources = [...new Set(events.map((e) => e.source).filter((s) => s))].sort();
    return sources.map((source) => ({ label: source, value: source }));
  }, [events]);

  const connectorFilters = useMemo(() => {
    if (!events || events.length === 0) return [];
    const connectors = [...new Set(events.map((e) => e.connector_name).filter((c) => c))].sort();
    return connectors.map((connector) => ({ label: connector, value: connector }));
  }, [events]);

  const projectFilters = useMemo(() => {
    if (!events || events.length === 0) return [];
    const projects = [...new Set(events.map((e) => e.project_name).filter((p) => p))].sort();
    return projects.map((project) => ({ label: project, value: project }));
  }, [events]);

  const statusFilters = useMemo(() => {
    return [
      { label: 'Unresolved', value: false },
      { label: 'Resolved', value: true },
    ];
  }, []);

  /**
   * Column definitions for TGDataTable
   */
  const columns = [
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      fixed: 'left',
      render: (id) => <span style={{ fontFamily: 'Monaco, monospace', fontSize: 11 }}>#{id}</span>,
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'received_at',
      title: 'Received At',
      dataIndex: 'received_at',
      width: 180,
      render: (text) => (text ? new Date(text).toLocaleString() : '-'),
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'connector_name',
      title: 'Connector',
      dataIndex: 'connector_name',
      width: 180,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text || 'Unknown'}>
          <Text>{text || 'Unknown'}</Text>
        </Tooltip>
      ),
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: connectorFilters,
    },
    {
      key: 'project_name',
      title: 'Project',
      dataIndex: 'project_name',
      width: 150,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text || 'Unknown'}>
          <Text>{text || 'Unknown'}</Text>
        </Tooltip>
      ),
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: projectFilters,
    },
    {
      key: 'error_type',
      title: 'Error Type',
      dataIndex: 'error_type',
      width: 160,
      align: 'center',
      render: (errorType) => {
        const colorMap = {
          missing_field: colors.status.fail,
          format_error: colors.status.warning,
          type_error: '#ff7a45',
          validation_error: '#eb2f96',
        };
        return (
          <Tag color={colorMap[errorType] || 'default'} style={{ fontSize: 11 }}>
            {errorType}
          </Tag>
        );
      },
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: errorTypeFilters,
    },
    {
      key: 'source',
      title: 'Source',
      dataIndex: 'source',
      width: 150,
      ellipsis: true,
      render: (text) => text || '-',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: sourceFilters,
    },
    {
      key: 'source_ip',
      title: 'Source IP',
      dataIndex: 'source_ip',
      width: 140,
      render: (ip) => ip || '-',
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'parse_error',
      title: 'Parse Error',
      dataIndex: 'parse_error',
      width: 250,
      ellipsis: true,
      render: (error) => (
        <Tooltip title={error}>
          <Text type="danger" style={{ fontSize: 11, fontFamily: 'Monaco, monospace' }}>
            {error || '-'}
          </Text>
        </Tooltip>
      ),
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'resolved',
      title: 'Status',
      dataIndex: 'resolved',
      width: 120,
      align: 'center',
      render: (resolved) =>
        resolved ? (
          <Tag color={colors.status.pass} icon={<CheckCircleOutlined />}>
            Resolved
          </Tag>
        ) : (
          <Tag color={colors.status.fail} icon={<CloseCircleOutlined />}>
            Unresolved
          </Tag>
        ),
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: statusFilters,
    },
    {
      key: 'resolved_at',
      title: 'Resolved At',
      dataIndex: 'resolved_at',
      width: 180,
      render: (text) => (text ? new Date(text).toLocaleString() : '-'),
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <a
              onClick={() => {
                setSelectedEvent(record);
                setDetailModalVisible(true);
              }}
              style={{ color: colors.primary[500] }}
            >
              Details
            </a>
          </Tooltip>
          {!record.resolved && (
            <Button
              size="small"
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => {
                setSelectedEvent(record);
                setResolveModalVisible(true);
              }}
            >
              Resolve
            </Button>
          )}
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <TrustGridPage
      title="Diagnostics - Dead-Letter Queue"
      subtitle="Monitor and manage malformed events that failed validation"
      breadcrumbs={breadcrumbs}
      showRefresh
      onRefresh={fetchData}
    >
      <ProjectSelector />

      {/* Summary Statistics */}
      {summary && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <DataCard
              title="Total Events"
              value={summary.summary?.total || 0}
              icon={<BugOutlined />}
            />
          </Col>
          <Col span={8}>
            <DataCard
              title="Unresolved"
              value={summary.summary?.unresolved || 0}
              valueColor={colors.status.fail}
              icon={<CloseCircleOutlined />}
            />
          </Col>
          <Col span={8}>
            <DataCard
              title="Resolved"
              value={summary.summary?.resolved || 0}
              valueColor={colors.status.pass}
              icon={<CheckCircleOutlined />}
            />
          </Col>
        </Row>
      )}

      {/* Events Table */}
      <TGDataTable
        data={events}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination="client"
        pageSize={20}
        showExport
        showColumnConfig
        showFilters
        exportFilename="diagnostics-dlq"
        presetKey="trustgrid.diagnostics"
        onRefresh={fetchData}
      />

      {/* Event Detail Modal */}
      <DetailModal
        visible={detailModalVisible}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedEvent(null);
        }}
        title="Event Details"
        width={800}
        metadata={
          selectedEvent
            ? [
                { label: 'Event ID', value: selectedEvent.id },
                { label: 'Connector', value: selectedEvent.connector_name || 'Unknown' },
                { label: 'Project', value: selectedEvent.project_name || 'Unknown' },
                {
                  label: 'Error Type',
                  value: <Tag color={colors.status.fail}>{selectedEvent.error_type}</Tag>,
                },
                { label: 'Source', value: selectedEvent.source || '-' },
                { label: 'Source IP', value: selectedEvent.source_ip || '-' },
                {
                  label: 'Received At',
                  value: selectedEvent.received_at
                    ? new Date(selectedEvent.received_at).toLocaleString()
                    : '-',
                },
                {
                  label: 'Status',
                  value: selectedEvent.resolved ? (
                    <Tag color={colors.status.pass}>Resolved</Tag>
                  ) : (
                    <Tag color={colors.status.fail}>Unresolved</Tag>
                  ),
                },
                ...(selectedEvent.resolved
                  ? [
                      {
                        label: 'Resolved At',
                        value: selectedEvent.resolved_at
                          ? new Date(selectedEvent.resolved_at).toLocaleString()
                          : '-',
                      },
                      {
                        label: 'Resolution Notes',
                        value: selectedEvent.resolution_notes || '-',
                        span: 2,
                      },
                    ]
                  : []),
              ]
            : []
        }
      >
        {selectedEvent && (
          <>
            <div style={{ marginTop: 16 }}>
              <Title level={5}>Parse Error:</Title>
              <Card size="small">
                <Paragraph
                  style={{
                    whiteSpace: 'pre-wrap',
                    color: colors.status.fail,
                    fontFamily: 'monospace',
                  }}
                >
                  {selectedEvent.parse_error}
                </Paragraph>
              </Card>
            </div>

            <div style={{ marginTop: 16 }}>
              <Title level={5}>Event Data:</Title>
              <Card size="small">
                <pre style={{ maxHeight: '300px', overflow: 'auto', margin: 0 }}>
                  {JSON.stringify(selectedEvent.event_data, null, 2)}
                </pre>
              </Card>
            </div>
          </>
        )}
      </DetailModal>

      {/* Resolve Modal */}
      <Modal
        title="Resolve Event"
        open={resolveModalVisible}
        onOk={handleResolve}
        onCancel={() => {
          setResolveModalVisible(false);
          setResolutionNotes('');
          setSelectedEvent(null);
        }}
        okText="Mark as Resolved"
        okType="primary"
        cancelText="Cancel"
      >
        {selectedEvent && (
          <div>
            <Paragraph>
              Mark event <Text strong>#{selectedEvent.id}</Text> as resolved?
            </Paragraph>
            <div style={{ marginTop: 16 }}>
              <Text strong>Resolution Notes (optional):</Text>
              <TextArea
                rows={4}
                placeholder="Describe how this issue was resolved..."
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                style={{ marginTop: 8 }}
              />
            </div>
          </div>
        )}
      </Modal>
    </TrustGridPage>
  );
}

export default Diagnostics;
